import React, { useCallback } from 'react'
import styles from './index.module.scss'
import { Spinner } from '../../pages/shared/Spinner'
import ReportChart from './ReportChart'
import QUERY_KEYS from '../../graphql/queryKeys'
import { ArrayParam, BooleanParam, NumericArrayParam, useQueryParams } from 'use-query-params'
import useQuery from '../../hooks/useQuery'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'
import reportsTicketOverviewQuery from '../../graphql/queries/reportsTicketOverview'
import { pastYears } from '../../utilities/randomUtilities'
import reportsFiltersQuery from '../../graphql/queries/reportsFilters'
import ReportsFilterForm from './reports-filter-form'
import { COLORS } from '../../utilities/constants'

function TicketOverview () {
  const [queryParams, setQueryParams] = useQueryParams({
    years: NumericArrayParam,
    resourceTypes: ArrayParam,
    ticketTypes: ArrayParam,
    status: ArrayParam,
    drivers: ArrayParam,
    yearToDate: BooleanParam
  })
  // Curried function to make sure we are always using 'replace' update.
  const setQueryParamsReplace = useCallback((params) => {
    setQueryParams(params, 'replace')
  }, [setQueryParams])

  const { data: reportData, isFetching } = useQuery([
    QUERY_KEYS.reportsTicketOverview,
    queryParams.years?.length > 0 ? queryParams.years?.map((e) => Number(e)) : null,
    queryParams.resourceTypes?.length > 0 ? queryParams.resourceTypes : null,
    queryParams.ticketTypes?.length > 0 ? queryParams.ticketTypes : null,
    queryParams.status?.length > 0 ? queryParams.status : null,
    queryParams.drivers?.length > 0 ? queryParams.drivers : null,
    queryParams.yearToDate
  ],
  reportsTicketOverviewQuery,
  {
    initialData: { reportsTicketOverview: [] },
    onError: (error) => {
      captureErrorAndNotify(error, 'Failed to fetch reports data')
    }
  })

  const { data: { reportsFilters = [] } = {}, isFetching: isFetchingReportsFilters } = useQuery(
    [QUERY_KEYS.reportsFilters],
    reportsFiltersQuery,
    {
      onError (error) {
        captureErrorAndNotify(error, 'Failed to retrieve resource types')
      },
      placplaceholderData: {
        reportFilters: {
          resources: [], ticketTypes: [], drivers: []
        }
      }
    }
  )

  const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const years = queryParams?.years?.length > 0 ? queryParams?.years?.map((a) => ({ value: Number(a), label: a })) : pastYears(3)

  const getCategories = () => {
    let mn = reportData?.reportsTicketOverview?.ticketsByMonth?.map((tickets) => tickets.month)
    mn = Array.from(new Set(mn)).sort((a, b) => a - b)
    return mn.map((month) => monthLabels[month - 1])
  }

  return (
    <div className={styles.reportsDiv}>
      <div>
        <ReportsFilterForm
          handleInputChange={setQueryParamsReplace}
          showOptions={['years', 'resourceTypes', 'ticketTypes', 'status', 'drivers', 'yearToDate']}
          options={{
            resourceTypes: reportsFilters?.resources?.map(opt => ({
              label: `${opt.name} (${opt.shortCode})`,
              value: opt.name
            })) || [],
            ticketTypes: reportsFilters?.ticketTypes?.map(opt => ({
              label: `${opt.name} (${opt.shortCode})`,
              value: opt.name
            })) || [],
            drivers: reportsFilters?.drivers?.map(opt => ({
              label: `${opt.name}`,
              value: opt.name
            })) || []
          }}
          initialValues={queryParams}
          clearSearch={() => setQueryParamsReplace({
            years: undefined,
            resourceTypes: undefined,
            ticketTypes: undefined,
            status: undefined,
            drivers: undefined,
            yearToDate: undefined
          })}
        />
      </div>
      <div className={styles.reportCharts}>
        <Spinner isFetching={reportData === undefined || isFetching || isFetchingReportsFilters}>
          <div style={{ width: '100%' }}>
            <div className={styles.chartContainer}>
              <ReportChart
                categories={getCategories()}
                series={
                  years.map((year, i) => {
                    const colorKeys = Object.keys(COLORS || {})
                    const yearData = reportData?.reportsTicketOverview?.ticketsByMonth?.filter(tickets => tickets.year === year.value)
                    const monthData = getCategories().map((month) => yearData?.find(item => item.month === (monthLabels.indexOf(month) + 1))?.totalTickets)
                    return {
                      name: year.label,
                      data: yearData ? monthData : Array(12).fill(0),
                      color: COLORS[colorKeys?.at(i)]
                    }
                  })
                }
                horizontal={true}
                height={350}
                type='line'
              />
            </div>
          </div>
          <div className={styles.chartContainer}>
            <ReportChart
              title='Total Tickets By Assets'
              categories={reportData?.reportsTicketOverview?.ticketsByResources?.map(tickets => tickets.resourceName) || []}
              series={[
                {
                  name: 'Total Tickets',
                  data: reportData?.reportsTicketOverview?.ticketsByResources?.map(tickets => tickets.count) || [],
                  color: COLORS?.PRIMARY_DK
                }
              ]}
              horizontal={true}
              height={350}
              type='doughnut'
              displayTotal
            />
          </div>
          <div className={styles.chartContainer}>
            <ReportChart
              title='Total Tickets'
              categories={reportData?.reportsTicketOverview?.ticketsByYear?.map(tickets => tickets.year) || []}
              series={[
                {
                  name: 'Average Per Day',
                  data: reportData?.reportsTicketOverview?.ticketsByYear?.map(tickets => tickets.avgTicketsPerDay) || [],
                  color: COLORS?.PRIMARY_DK
                },
                {
                  name: 'Total',
                  data: reportData?.reportsTicketOverview?.ticketsByYear?.map(tickets => tickets.totalTickets) || [],
                  color: COLORS?.PRIMARY
                }
              ]}
              height={350}
            />
          </div>
        </Spinner>
      </div>
    </div>
  )
}

export default TicketOverview
